// 🔥 Firestore 등급별 가격 관리 서비스
// 등급별 가격 정보를 Firestore에 저장하고 관리하는 서비스

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { DevicePriceTable, GradePriceInfo, DeviceGrade, DeviceTypePriceTable } from '../types/gradeSystem';
import { 
  UNIFIED_DEVICE_DATA,
  getDevicesByCategory,
  findDeviceByLegacyFormat,
  buildPriceKey
} from '../constants/unifiedDeviceData';

// Firestore 컬렉션 이름 (브랜드/디바이스타입별로 분리된 컬렉션 사용; 레거시는 폴백 용도)
const COLLECTIONS = {
  GRADE_PRICES: 'grade_prices', // 레거시: 단일 컬렉션 (읽기 폴백만)
  GRADE_PRICES_APPLE: 'grade_prices_apple',
  GRADE_PRICES_SAMSUNG: 'grade_prices_samsung',
  GRADE_PRICES_APPLE_TABLET: 'grade_prices_apple_tablet',
  GRADE_PRICES_SAMSUNG_TABLET: 'grade_prices_samsung_tablet',
  DEVICE_MODELS: 'device_models',
  PRICE_HISTORY: 'price_history'
} as const;

// 브랜드/디바이스타입별 컬렉션명 헬퍼
const getBrandCollectionName = (brand: 'Apple' | 'Samsung' | 'LG', deviceType?: string): string => {
  if (brand === 'LG') {
    throw new Error('LG 브랜드는 레거시 컬렉션을 지원하지 않습니다. unified_prices를 사용하세요.');
  }
  if (deviceType === '태블릿') {
    return brand === 'Apple' ? COLLECTIONS.GRADE_PRICES_APPLE_TABLET : COLLECTIONS.GRADE_PRICES_SAMSUNG_TABLET;
  }
  return brand === 'Apple' ? COLLECTIONS.GRADE_PRICES_APPLE : COLLECTIONS.GRADE_PRICES_SAMSUNG;
};

// ID 정규화
const normalizeId = (v: string) => v.replace(/\s+/g, '_').toLowerCase();

// 🔥 모델명 기반 브랜드/시리즈 추론 (통합 데이터 사용)
const inferBrandAndSeries = (modelName: string): { brand: 'Apple' | 'Samsung' | 'LG'; series?: string } => {
  const name = String(modelName || '').trim();

  // 통합 데이터에서 해당 모델 찾기
  const device = UNIFIED_DEVICE_DATA.find(d =>
    d.modelKo === name ||
    d.modelEn === name ||
    d.searchTerms.some(term => name.toLowerCase().includes(term))
  );

  if (device) {
    return { brand: device.brand, series: device.series };
  }

  // 폴백: 패턴 매칭
  if (/^(iPhone|아이폰)/i.test(name)) {
    return { brand: 'Apple' };
  }
  if (/(Galaxy|갤럭시)/i.test(name)) {
    // 통합 데이터에서 삼성 기기의 시리즈 목록 추출
    const samsungSeries = Array.from(new Set(
      UNIFIED_DEVICE_DATA
        .filter(d => d.brand === 'Samsung')
        .map(d => d.series)
    ));

    for (const series of samsungSeries) {
      const ko = series.replace('Galaxy', '갤럭시');
      if (name.startsWith(series) || name.startsWith(ko) || name.includes(`${series} `) || name.includes(`${ko} `)) {
        return { brand: 'Samsung', series };
      }
    }
    return { brand: 'Samsung' };
  }
  if (/^LG\s+/i.test(name) || /\bLG\b/i.test(name)) {
    return { brand: 'LG' };
  }

  // 기본: Apple로 간주 (하위호환)
  return { brand: 'Apple' };
};

/**
 * 등급별 가격 정보를 Firestore에 저장
 */
export const saveGradePrices = async (
  deviceType: string,
  deviceModel: string,
  storage: string,
  prices: GradePriceInfo
): Promise<void> => {
  try {
    const { brand, series } = inferBrandAndSeries(deviceModel);
    const newDocId = normalizeId(`${brand}_${deviceType}_${series || 'na'}_${deviceModel}_${storage}`);
    const coll = getBrandCollectionName(brand, deviceType);
    const docRef = doc(db, coll, newDocId);
    
    await setDoc(docRef, {
      deviceType,
      deviceModel,
      storage,
      gradePrices: prices,
      isActive: true,
      version: 'live',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: 'admin', // 실제로는 현재 로그인한 관리자 ID
      brand,
      series: series || null,
    });
    
    console.log(`✅ 등급별 가격 저장 완료: ${deviceType} - ${deviceModel} - ${storage}`);
  } catch (error) {
    console.error('❌ 등급별 가격 저장 실패:', error);
    throw error;
  }
};

/**
 * 특정 기기의 등급별 가격 정보 조회
 */
export const getGradePrices = async (
  deviceType: string,
  deviceModel: string,
  storage: string
): Promise<GradePriceInfo | null> => {
  try {
    const { brand, series } = inferBrandAndSeries(deviceModel);
    const newDocId = normalizeId(`${brand}_${deviceType}_${series || 'na'}_${deviceModel}_${storage}`);
    // 1) 브랜드/디바이스타입별 컬렉션 우선 조회
    const coll = getBrandCollectionName(brand, deviceType);
    let docSnap = await getDoc(doc(db, coll, newDocId));
    if (!docSnap.exists()) {
      // 2) 레거시 키(브랜드 확장 전) 폴백
      const legacyDocId = normalizeId(`${deviceType}_${deviceModel}_${storage}`);
      docSnap = await getDoc(doc(db, COLLECTIONS.GRADE_PRICES, legacyDocId));
    }
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return (data.gradePrices || data.prices) as GradePriceInfo;
    }
    
    return null;
  } catch (error) {
    console.error('❌ 등급별 가격 조회 실패:', error);
    throw error;
  }
};

/**
 * 모든 기기의 등급별 가격 정보 조회
 */
export const getAllGradePrices = async (): Promise<DevicePriceTable[]> => {
  try {
    // 브랜드별 컬렉션을 각각 조회 후 병합. 레거시 컬렉션은 최후 폴백으로만 사용
    const mapSnapshot = (snap: any): DevicePriceTable[] => {
      const rows: DevicePriceTable[] = [];
      snap.forEach((d: any) => {
        const data = d.data();
        rows.push({
          id: d.id,
          deviceType: data.deviceType,
          deviceModel: data.deviceModel,
          storage: data.storage || '기본',
          gradePrices: data.prices || data.gradePrices,
          isActive: data.isActive !== false,
          version: data.version || 'live',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy
        });
      });
      return rows;
    };

    const [applePhoneSnap, samsungPhoneSnap, appleTabletSnap, samsungTabletSnap] = await Promise.all([
      getDocs(query(collection(db, COLLECTIONS.GRADE_PRICES_APPLE), orderBy('deviceType'))),
      getDocs(query(collection(db, COLLECTIONS.GRADE_PRICES_SAMSUNG), orderBy('deviceType'))),
      getDocs(query(collection(db, COLLECTIONS.GRADE_PRICES_APPLE_TABLET), orderBy('deviceType'))),
      getDocs(query(collection(db, COLLECTIONS.GRADE_PRICES_SAMSUNG_TABLET), orderBy('deviceType'))),
    ]);

    let rows = [
      ...mapSnapshot(applePhoneSnap),
      ...mapSnapshot(samsungPhoneSnap),
      ...mapSnapshot(appleTabletSnap),
      ...mapSnapshot(samsungTabletSnap),
    ];

    // 레거시 폴백: 신규 컬렉션이 비어있을 때만 사용
    if (rows.length === 0) {
      const legacySnap = await getDocs(query(collection(db, COLLECTIONS.GRADE_PRICES), orderBy('deviceType')));
      rows = mapSnapshot(legacySnap);
    }

    return rows;
  } catch (error) {
    console.error('❌ 전체 등급별 가격 조회 실패:', error);
    throw error;
  }
};

/**
 * 등급별 가격 정보 업데이트
 */
export const updateGradePrices = async (
  deviceType: string,
  deviceModel: string,
  storage: string,
  prices: GradePriceInfo
): Promise<void> => {
  try {
    const { brand, series } = inferBrandAndSeries(deviceModel);
    const newDocId = normalizeId(`${brand}_${deviceType}_${series || 'na'}_${deviceModel}_${storage}`);
    const coll = getBrandCollectionName(brand, deviceType);
    const docRef = doc(db, coll, newDocId);
    
    await updateDoc(docRef, {
      gradePrices: prices,
      updatedAt: serverTimestamp(),
      updatedBy: 'admin'
    });
    
    console.log(`✅ 등급별 가격 업데이트 완료: ${deviceType} - ${deviceModel} - ${storage}`);
  } catch (error) {
    console.error('❌ 등급별 가격 업데이트 실패:', error);
    throw error;
  }
};

/**
 * 등급별 가격 정보 삭제
 */
export const deleteGradePrices = async (
  deviceType: string,
  deviceModel: string,
  storage: string
): Promise<void> => {
  try {
    const { brand, series } = inferBrandAndSeries(deviceModel);
    const newDocId = normalizeId(`${brand}_${deviceType}_${series || 'na'}_${deviceModel}_${storage}`);
    const coll = getBrandCollectionName(brand, deviceType);
    const docRef = doc(db, coll, newDocId);
    
    await deleteDoc(docRef);
    
    console.log(`✅ 등급별 가격 삭제 완료: ${deviceType} - ${deviceModel} - ${storage}`);
  } catch (error) {
    console.error('❌ 등급별 가격 삭제 실패:', error);
    throw error;
  }
};

/**
 * 가격 변경 이력 저장
 */
export const savePriceHistory = async (
  deviceType: string,
  deviceModel: string,
  storage: string,
  oldPrices: GradePriceInfo,
  newPrices: GradePriceInfo,
  reason: string
): Promise<void> => {
  try {
    const historyRef = doc(collection(db, COLLECTIONS.PRICE_HISTORY));
    
    await setDoc(historyRef, {
      deviceType,
      deviceModel,
      storage,
      oldPrices,
      newPrices,
      reason,
      changedAt: serverTimestamp(),
      changedBy: 'admin'
    });
    
    console.log(`✅ 가격 변경 이력 저장 완료: ${deviceType} - ${deviceModel} - ${storage}`);
  } catch (error) {
    console.error('❌ 가격 변경 이력 저장 실패:', error);
    throw error;
  }
};

/**
 * 가격 조회 API 서비스 (Firebase Functions 사용)
 */
export const priceApiService = {
  // 가격 조회
  async lookupPrice(deviceType: string, deviceModel: string, storage: string, grade: DeviceGrade): Promise<number | null> {
    try {
      const response = await fetch(
        `/api/grade-prices/lookup?deviceType=${encodeURIComponent(deviceType)}&deviceModel=${encodeURIComponent(deviceModel)}&storage=${encodeURIComponent(storage)}&grade=${grade}`
      );
      const data = await response.json();
      
      if (data.success) {
        return data.price;
      }
      return null;
    } catch (error) {
      console.error('가격 조회 API 실패:', error);
      return null;
    }
  },

  // 모든 가격 정보 조회
  async getAllPrices(): Promise<DevicePriceTable[]> {
    try {
      const response = await fetch('/api/grade-prices/all');
      const data = await response.json();
      
      if (data.success) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('전체 가격 조회 API 실패:', error);
      return [];
    }
  },

  // 가격 저장
  async savePrices(deviceType: string, deviceModel: string, storage: string, prices: GradePriceInfo): Promise<boolean> {
    try {
      const response = await fetch('/api/grade-prices/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deviceType, deviceModel, storage, prices }),
      });
      const data = await response.json();
      
      return data.success;
    } catch (error) {
      console.error('가격 저장 API 실패:', error);
      return false;
    }
  },

  // 일괄 가격 업데이트
  async bulkUpdatePrices(updates: Array<{deviceType: string, deviceModel: string, storage: string, prices: GradePriceInfo}>): Promise<{success: boolean, updatedCount: number, errors: string[]}> {
    try {
      const response = await fetch('/api/grade-prices/bulk-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      const data = await response.json();
      
      return data;
    } catch (error) {
      console.error('일괄 가격 업데이트 API 실패:', error);
      return { success: false, updatedCount: 0, errors: ['API 호출 실패'] };
    }
  },

  // 기본 데이터 초기화
  async initializeDefaultPrices(): Promise<boolean> {
    try {
      const response = await fetch('/api/grade-prices/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      return data.success;
    } catch (error) {
      console.error('기본 데이터 초기화 API 실패:', error);
      return false;
    }
  }
};

// =============================
// 🔧 Firestore 직접 연동 서비스 (Mock API 대체용 동일 인터페이스)
// =============================

// 평탄 데이터(DevicePriceTable[])를 UI용 구조(DeviceTypePriceTable[])로 변환
// 동일 모델×용량 중복 행(레거시 ID와 브랜드 확장 ID 공존)을 제거하고 우선순위 규칙으로 단일화한다.
const mapToDeviceTypePriceTables = (flat: DevicePriceTable[]): DeviceTypePriceTable[] => {
  // deviceType 별 버킷
  const typeToKeyedMap = new Map<string, Map<string, DevicePriceTable>>();

  const countNonNullPrices = (row: DevicePriceTable): number => {
    return Object.values(row.gradePrices || {}).filter((v) => v !== null && v !== undefined).length;
  };

  flat.forEach((row) => {
    if (!typeToKeyedMap.has(row.deviceType)) {
      typeToKeyedMap.set(row.deviceType, new Map());
    }
    const keyed = typeToKeyedMap.get(row.deviceType)!;
    const key = `${String(row.deviceModel)}|||${String(row.storage)}`;
    const existing = keyed.get(key);

    if (!existing) {
      keyed.set(key, row);
    } else {
      const newId = String(row.id || '');
      const oldId = String(existing.id || '');
      const newIsBranded = newId.startsWith('apple_') || newId.startsWith('samsung_');
      const oldIsBranded = oldId.startsWith('apple_') || oldId.startsWith('samsung_');

      // 우선순위:
      // 1) 브랜드 확장 ID가 레거시 ID보다 우선
      // 2) non-null 가격 항목이 더 많은 쪽 우선
      // 3) updatedAt 최신 우선 (가능한 경우)
      // 4) 그 외에는 신규로 덮어씀
      const newNonNull = countNonNullPrices(row);
      const oldNonNull = countNonNullPrices(existing);

      const getUpdatedAtMs = (r: any): number => {
        const u = r?.updatedAt;
        try {
          if (!u) return 0;
          if (typeof u.toMillis === 'function') return u.toMillis();
          if (u.seconds) return u.seconds * 1000 + (u.nanoseconds ? Math.floor(u.nanoseconds / 1e6) : 0);
          const t = new Date(u).getTime();
          return isNaN(t) ? 0 : t;
        } catch {
          return 0;
        }
      };

      const newUpdated = getUpdatedAtMs(row);
      const oldUpdated = getUpdatedAtMs(existing);

      let takeNew = false;
      if (newIsBranded && !oldIsBranded) takeNew = true;
      else if (newIsBranded === oldIsBranded) {
        if (newNonNull > oldNonNull) takeNew = true;
        else if (newNonNull === oldNonNull && newUpdated >= oldUpdated) takeNew = true;
      }

      if (takeNew) {
        keyed.set(key, row);
      }
    }
  });

  // UI 구조로 변환
  const result: DeviceTypePriceTable[] = [];
  typeToKeyedMap.forEach((keyed, deviceType) => {
    const deviceModels: DeviceTypePriceTable['deviceModels'] = Array.from(keyed.values()).map((row) => ({
      deviceType: row.deviceType,
      deviceModel: row.deviceModel,
      storage: row.storage,
      prices: row.gradePrices,
    }));
    result.push({ deviceType, deviceModels });
  });

  return result;
};

// 내부 유틸: 모델/용량 기반 기본 가격 산출 (간단 규칙)
const getBasePriceForModel = (model: string, storage: string): number => {
  const modelPrices: Record<string, number> = {
    '아이폰17': 1300000,
    '아이폰17 AIR': 1400000,
    '아이폰17 PRO': 1600000,
    '아이폰17 PRO MAX': 1800000,
    '아이폰16': 1200000,
    '아이폰16 Plus': 1300000,
    '아이폰16 PRO': 1500000,
    '아이폰16 PRO MAX': 1700000,
    '아이폰16e': 1000000,
    '아이폰15': 1100000,
    '아이폰15 Plus': 1200000,
    '아이폰15 PRO': 1400000,
    '아이폰15 PRO MAX': 1600000,
    '아이폰14': 1000000,
    '아이폰14 Plus': 1100000,
    '아이폰14 PRO': 1300000,
    '아이폰14 PRO MAX': 1500000,
    '아이폰13': 900000,
    '아이폰13 MINI': 800000,
    '아이폰13 PRO': 1100000,
    '아이폰13 PRO MAX': 1300000,
    '아이폰12': 800000,
    '아이폰12 MINI': 700000,
    '아이폰12 PRO': 1000000,
    '아이폰12 PRO MAX': 1200000,
    '아이폰11': 700000,
    '아이폰11 PRO': 900000,
    '아이폰11 PRO MAX': 1100000,
    '아이폰X': 600000,
    '아이폰 XS': 700000,
    '아이폰 XS MAX': 800000,
    '아이폰 XR': 500000,
    '아이폰8': 400000,
    '아이폰8 Plus': 450000,
    '아이폰7': 300000,
    '아이폰7 Plus': 350000,
    '아이폰6': 200000,
    '아이폰6 Plus': 250000,
    '아이폰6S': 250000,
    '아이폰6S Plus': 300000,
    '아이폰SE': 200000,
    '아이폰SE2': 300000,
    '아이폰SE3': 400000,
  };

  const storageMultipliers: Record<string, number> = {
    '16GB': 0.8,
    '32GB': 0.85,
    '64GB': 0.9,
    '128GB': 1.0,
    '256GB': 1.2,
    '512GB': 1.4,
    '1TB': 1.6,
  };

  const base = modelPrices[model] ?? 500000;
  const mul = storageMultipliers[storage] ?? 1.0;
  return Math.round(base * mul);
};

export const firestorePriceApiService = {
  // 모든 가격 정보 조회 (Firestore 직접)
  async getAllPrices(): Promise<DeviceTypePriceTable[]> {
    const flat = await getAllGradePrices();
    return mapToDeviceTypePriceTables(flat);
  },

  // 가격 조회 (단건)
  async lookupPrice(deviceType: string, deviceModel: string, storage: string, grade: DeviceGrade): Promise<number | null> {
    const prices = await getGradePrices(deviceType, deviceModel, storage);
    if (!prices) return null;
    return prices[grade] ?? null;
  },

  // 가격 저장/업데이트
  async savePrices(deviceType: string, deviceModel: string, storage: string, prices: GradePriceInfo): Promise<boolean> {
    await saveGradePrices(deviceType, deviceModel, storage, prices);
    return true;
  },

  // 일괄 가격 업데이트
  async bulkUpdatePrices(updates: Array<{deviceType: string, deviceModel: string, storage: string, prices: GradePriceInfo}>): Promise<{success: boolean, updatedCount: number, errors: string[]}> {
    const errors: string[] = [];
    let updatedCount = 0;
    for (const u of updates) {
      try {
        await saveGradePrices(u.deviceType, u.deviceModel, u.storage, u.prices);
        updatedCount++;
      } catch (e: any) {
        errors.push(`${u.deviceType}-${u.deviceModel}-${u.storage}: ${String(e?.message || e)}`);
      }
    }
    return { success: errors.length === 0, updatedCount, errors };
  },

  // 단일 행 삭제 (모델×용량)
  async deletePrice(deviceType: string, deviceModel: string, storage: string): Promise<boolean> {
    try {
      await deleteGradePrices(deviceType, deviceModel, storage);
      return true;
    } catch (error) {
      console.error('가격 삭제 실패:', error);
      return false;
    }
  },

  // 🔥 기본 데이터 초기화 (통합 데이터 기반)
  async initializeDefaultPrices(): Promise<boolean> {
    try {
      const deviceType = '스마트폰';
      const appleDevices = UNIFIED_DEVICE_DATA.filter(d => 
        d.brand === 'Apple' && d.category === '스마트폰'
      );
      
      for (const device of appleDevices) {
        const base = getBasePriceForModel(device.modelKo, device.storage);
        const prices: GradePriceInfo = {
          A: Math.round(base * 1.0),
          B: Math.round(base * 0.8),
          C: Math.round(base * 0.6),
          D: Math.round(base * 0.4),
          E: Math.round(base * 0.2),
        };
        await saveGradePrices(deviceType, device.modelKo, device.storage, prices);
      }
      return true;
    } catch (error) {
      console.error('기본 데이터 초기화 실패:', error);
      return false;
    }
  },

  // 🔥 브랜드별 초기 데이터 생성 (통합 데이터 기반)
  async initializeBrandPrices(brand: 'Apple' | 'Samsung' | 'LG'): Promise<boolean> {
    try {
      if (brand === 'Apple') return this.initializeDefaultPrices();
      
      const deviceType = '스마트폰';
      const samsungDevices = UNIFIED_DEVICE_DATA.filter(d => 
        d.brand === 'Samsung' && d.category === '스마트폰'
      );
      
      for (const device of samsungDevices) {
        const prices: GradePriceInfo = { A: null, B: null, C: null, D: null, E: null };
        await saveGradePrices(deviceType, device.modelKo, device.storage, prices);
      }
      return true;
    } catch (error) {
      console.error('브랜드별 초기 데이터 생성 실패:', error);
      return false;
    }
  },

  // 🔥 태블릿 초기 데이터 생성 (통합 데이터 기반)
  async initializeTabletPrices(brand: 'Apple' | 'Samsung' | 'LG'): Promise<boolean> {
    try {
      const deviceType = '태블릿';
      const tabletDevices = UNIFIED_DEVICE_DATA.filter(d => 
        d.brand === brand && d.category === '태블릿'
      );
      
      for (const device of tabletDevices) {
        const prices: GradePriceInfo = { A: null, B: null, C: null, D: null, E: null };

        // 연결성 정보가 있으면 storage에 포함
        const storageKey = device.connectivity 
          ? `${device.storage} - ${device.connectivity}`
          : device.storage;
          
        await saveGradePrices(deviceType, device.modelKo, storageKey, prices);
      }
      
      return true;
    } catch (error) {
      console.error('태블릿 초기 데이터 생성 실패:', error);
      return false;
    }
  },
};
